<template>
  <valkyrie-list
    :list-data="listData"
    :pagination="listPagination"
    :list-name="'Reservations'"
    :headers.sync="headers"
    :is-loading="loading"
    class="mt-5"
    @update-pagination="updatePagination"
  >
    <template v-slot:list-header>
      <default-list-header
        exportable
        filterable
        @clear-filters="clearFilters"
        @export-csv="exportCSV('Reservations')"
      >
        <template #type>
          <multiselect-filter
            filter-key="type"
            :items="reservationTypes"
            @apply-multiselect="updateFilters"
          />
        </template>
        <template #checkin>
          <date-filter
            filter-key="checkin"
            @apply-date-filter="updateFilters"
          />
        </template>
        <template #checkout>
          <date-filter
            filter-key="checkout"
            @apply-date-filter="updateFilters"
          />
        </template>
        <template #number-of-guests>
          <comparison-filter
            filter-key="guests_expected"
            @apply-comparison-filter="updateFilters"
          />
        </template>
      </default-list-header>
    </template>
    <!-- eslint-disable vue/valid-v-slot -->
    <template #item.propertyName="{ item }">
      {{ item.propertyName }}
    </template>
    <template #item.checkin="{ item }">
      {{ formatReservationDate(item.checkin, item.timezone) }}
    </template>
    <template #item.checkout="{ item }">
      {{ formatReservationDate(item.checkout, item.timezone) }}
    </template>
    <template v-slot:item.actions="{ item }">
      <v-btn
        icon
        color="error"
        @click="deleteItem(item)"
      >
        <v-icon>
          $faTrash
        </v-icon>
      </v-btn>
    </template>
    <template slot="empty-state">
      <empty-state
        icon="$faSignalStream"
        text="No reservations found"
      />
    </template>
  </valkyrie-list>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { DateTime } from 'luxon'
  import DateFilter from '@/components/filters/DateFilter.vue'
  import MultiselectFilter from '@/components/filters/MultiselectFilter.vue'
  import ComparisonFilter from '@/components/filters/ComparisonFilter.vue'
  import listMixin from '@/helpers/list-mixin'
  import ValkyrieList from '@/components/ValkyrieList.vue'
  import DefaultListHeader from '@/components/ValkyrieListDefaultHeader.vue'
  import EmptyState from '../../components/base/EmptyState.vue'

  export default {
    name: 'ReservationsList',
    components: {
      ValkyrieList,
      DefaultListHeader,
      EmptyState,
      DateFilter,
      MultiselectFilter,
      ComparisonFilter,
    },
    mixins: [listMixin],
    data: () => ({
      loading: false,
      search: '',
      payload: {
        listType: 'reservations',
        body: {
          filter: [],
        },
        fileName: 'reservations.csv',
      },
      filters: {},
      headers: [
        {
          text: 'Property Name',
          value: 'propertyName',
        },
        {
          text: 'Property ID',
          value: 'propertyID',
        },
        {
          text: 'Source',
          value: 'source',
        },
        {
          text: 'Type',
          value: 'type',
        },
        {
          text: "Check-in (Property's Local Time)",
          value: 'checkin',
        },
        {
          text: "Check-out (Property's Local Time)",
          value: 'checkout',
        },
        {
          text: '# of Guests',
          value: 'guestsExpected',
        },
        {
          text: 'Guest Name',
          value: 'guestInfo',
        },
        {
          text: 'Guest Phone',
          value: 'guestPhone',
        },
        { text: 'Actions', value: 'actions', align: 'center' },
      ],
      reservationTypes: [
        { name: 'Guest', value: 'guest' },
        { name: 'Owner', value: 'owner' },
        { name: 'Resident', value: 'resident' },
      ],
    }),
    computed: {
      ...mapGetters({
        listData: 'Reservations/listData',
        listPagination: 'Reservations/listPagination',
        selectedOrg: 'Permissions/currentSelectedOrg',
      }),
    },
    watch: {
      selectedOrg () {
        this.payload.body.filter = []
        this.fetchList()
      },
    },
    created () {
      this.$store.commit('Reservations/updatePagination', { perPage: 10, pageNum: 1 })
      this.fetchList()
    },
    methods: {
      updatePagination (payload) {
        this.$store.commit('Reservations/updatePagination', payload)
        this.fetchList()
      },
      async fetchList () {
        this.loading = true
        const pagination = { ...this.listPagination }
        delete pagination.total
        this.payload.body.pagination = pagination
        this.payload.body.filter.push({ key: 'organization_id', operator: '=', value: `${this.selectedOrg.id}` })
        await this.$store.dispatch('Reservations/fetchList', this.payload)
        this.loading = false
      },
      updateFilters (payload) {
        if (!payload.value) delete this.filters[payload.key]
        this.filters[payload.key] = payload
        this.payload.body.filter = Object.values(this.filters).reduce((acc, curr) => { if (curr.value) return [curr, ...acc]; else return acc }, [])
        this.fetchList()
      },
      clearFilters () {
        this.filters = {}
        this.payload.body.filter = []
        this.fetchList()
      },
      deleteItem (item) {
        this.$emit('reservation-deleted', item)
      },
      formatReservationDate (dateTime, timezone) {
        return DateTime.fromISO(dateTime).setZone(timezone).toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)
      },
      exportCSV (nameString) {
        const now = DateTime.local().toFormat('yyyyLLdd_HHmmss')
        const fileName = `${nameString}-${now}`
        this.payload.fileName = fileName
        const payload = { APIPath: `${this.$store.state.baseURL}/${this.payload.listType}/list/csv`, payload: this.payload.body, fileName: this.payload.fileName }
        this.$store.dispatch('getCSV', payload)
      },
    },
  }
</script>
